import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import {
  map,
  switchMap,
  catchError,
  tap,
  concatMap,
  withLatestFrom,
  mergeMap
} from 'rxjs/operators';

import * as ConsultationActions from './consultation.actions';
import * as ConsultationSelectors from './consultation.selectors';
import { APIService } from '../../services/api/api-service';
import { of, zip } from 'rxjs';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { AppState } from '../app.states';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  PATIENT_WEB_PAGES_URLS,
  notificationMessages
} from '@care-portals/data';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { ConsultationUtils } from '@care-portals/data';

/*
Flow #1: checkout -> evalRequiredForm  ->  getRequiredFormsuccess  ->  setRequiredForm -> setRequiredFormSuccess
Flow #2: consultation/start -> setRequiredForm -> setRequiredFormSuccess
*/
@Injectable()
export class ConsultationEffects {
  constructor(
    private actions$: Actions,
    private api: APIService,
    private router: Router,
    private store: Store<AppState>,
    private notification: NzNotificationService,
    private gtm: Angulartics2GoogleTagManager,
    private activatedRoute: ActivatedRoute
  ) {}

  /**
   * this will go to next question or finish the consultation if is last question
   */
  nextQuestionTrigger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        // saveFileSuccess (single file not files) action will not go to next action
        ConsultationActions.saveFilesSuccess,
        ConsultationActions.setAnswer
      ),
      map(() => ConsultationActions.nextQuestion())
    )
  );

  // Used to evaluate the required forms based on order number
  getRequiredForms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.getRequiredForms),
      switchMap(({ query }) =>
        this.api
          .getRequiredForms(query)
          .pipe(
            map(resp => ConsultationActions.getRequiredFormsSuccess(resp)),
            catchError(error => {
              console.error('Error', error);

              this.notification.error(
                notificationMessages.UnknownConsultation_PleaseTryAgain.title,
                notificationMessages.UnknownConsultation_PleaseTryAgain.message
              );

              this.router.navigateByUrl('/');

              return of(
                ConsultationActions.getRequiredFormsFailure({
                  error: error?.error?.message
                })
              );
            })
          )
      )
    )
  );

  /**
   * Questionnaires migrated to Pathwai never render here — the reducer has
   * already stripped them out of `forms.required`. If nothing legacy is left,
   * hand the patient straight over instead of loading a form we don't have.
   */
  getRequiredFormsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.getRequiredFormsSuccess),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getFormsState),
        this.store.select(ConsultationSelectors.getPathwaiHandoffUrl)
      ),
      map(([action, forms, pathwaiHandoffUrl]) =>
        forms.required.length === 0 && pathwaiHandoffUrl
          ? ConsultationActions.redirectToPathwai({ url: pathwaiHandoffUrl })
          : ConsultationActions.loadRequiredQuestions()
      )
    )
  );

  redirectToPathwai$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConsultationActions.redirectToPathwai),
        tap(({ url }) => {
          window.location.href = url;
        })
      ),
    { dispatch: false }
  );

  loadRequiredQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.loadRequiredQuestions),
      concatMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store.select(ConsultationSelectors.getSelectedFormType)
          ),
          switchMap(([action, formType]) => {
            if (!formType) {
              return of(
                ConsultationActions.loadRequiredQuestionsSuccess({
                  requiredQuestions: []
                })
              );
            }

            return this.api.getRequiredQuestions(formType).pipe(
              map(requiredQuestions => {
                return ConsultationActions.loadRequiredQuestionsSuccess({
                  requiredQuestions
                });
              }),
              catchError(error => {
                console.error('loadRequiredQuestionsFailure', error);
                return of(
                  ConsultationActions.loadRequiredQuestionsFailure({ error })
                );
              })
            );
          })
        )
      )
    )
  );

  loadRequiredQuestionsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConsultationActions.loadRequiredQuestionsSuccess),
        tap(({ requiredQuestions }) => {
          if (requiredQuestions.length === 0) {
            // no consultation is required
            this.router.navigate([PATIENT_WEB_PAGES_URLS.account.overview]);
            setTimeout(() => {
              this.notification.success(
                "You're All Set",
                'No further questions required at this time.',
                { nzDuration: 0 }
              );
            }, 500);
          }
        })
      ),
    { dispatch: false }
  );

  loadRequiredQuestionsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConsultationActions.loadRequiredQuestionsFailure),
        tap(res => {
          this.notification.error(
            notificationMessages.UnknownQuestion_PleaseTryAgain.title,
            notificationMessages.UnknownQuestion_PleaseTryAgain.message
          );
          this.router.navigateByUrl('/');
        })
      ),
    { dispatch: false }
  );

  saveForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.saveForm),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getConsultationState),
        this.store.select(ConsultationSelectors.isLastQuestion),
        this.store.select(ConsultationSelectors.getSelectedQuestionModel)
      ),
      switchMap(([action, state, completed, selectedQuestion]) => {
        const status = selectedQuestion?.reject
          ? 'rejected'
          : completed
          ? 'new'
          : 'incomplete';

        if (state._id) {
          return this.api
            .updateConsultation(state._id, {
              type: state.forms.required[state.forms.selectedIndex],
              answers: state.answers,
              status
            })
            .pipe(
              map(consultation =>
                ConsultationActions.saveFormSuccess({
                  completed,
                  consultation
                })
              ),
              catchError(error =>
                of(ConsultationActions.saveFormFailure(error))
              )
            );
        }

        return this.api
          .saveConsultation({
            type: state.forms.required[state.forms.selectedIndex],
            answers: state.answers,
            status
          })
          .pipe(
            map(consultation =>
              ConsultationActions.saveFormSuccess({
                completed,
                consultation
              })
            ),
            catchError(error => of(ConsultationActions.saveFormFailure(error)))
          );
      })
    )
  );

  saveFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.saveFormSuccess),
      withLatestFrom(this.store.select(ConsultationSelectors.isLastForm)),
      tap(([action]) => {
        if (action.completed) {
          this.gtm.eventTrack('completedSingle', {
            category: 'consultation',
            label: 'completedSingle',
            gtmCustom: {
              consultationType: action.consultation.type
            }
          });
        }
      }),
      map(([action, isLast]) => {
        if (isLast && action.completed) {
          // that means no more forms to answer
          return ConsultationActions.completedAllForms();
        } else if (action.completed) {
          return ConsultationActions.nextForm();
        }

        return ConsultationActions.nullAction();
      })
    )
  );

  saveFormFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.saveFormFailure),
      tap(({ error }) =>
        this.notification.error(
          notificationMessages.SavingFailed.title,
          notificationMessages.SavingFailed.message
        )
      )
    )
  );

  nextQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.nextQuestion),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getRequiredQuestions),
        this.store.select(ConsultationSelectors.getSelectedQuestionType),
        this.store.select(ConsultationSelectors.getAnswersState)
      ),
      mergeMap(([action, requiredQuestions, curQuestionType, answers]) => {
        ConsultationUtils.answers = answers;
        ConsultationUtils.requiredQuestions = requiredQuestions;
        const next = ConsultationUtils.getNext(curQuestionType);

        return [
          ConsultationActions.saveForm(),
          ConsultationActions.setNextQuestion({ questionType: next }),
          next
            ? ConsultationActions.nextQuestionSuccess({
                questionType: next,
                qs: ConsultationUtils.getIndexOfQuestion(next)
              })
            : ConsultationActions.nullAction()
        ];
      })
    )
  );

  setDraftConsult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.setDraftConsult),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getRequiredQuestions),
        this.store.select(ConsultationSelectors.getSelectedQuestionType),
        this.store.select(ConsultationSelectors.getAnswersState)
      ),
      mergeMap(([action, questions, curQuestionType, answers]) => {
        const questionStack = ConsultationUtils.getQuestionStackFromAnswers(
          questions,
          answers
        );

        const curQuestionAnswered = questionStack[questionStack.length - 1];

        const next = curQuestionAnswered;

        return [
          ConsultationActions.setNextQuestion({ questionType: next }),
          next
            ? ConsultationActions.nextQuestionSuccess({
                questionType: next,
                qs: ConsultationUtils.getIndexOfQuestion(next)
              })
            : ConsultationActions.nullAction()
        ];
      })
    )
  );

  nextQuestionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ConsultationActions.nextQuestionSuccess,
          ConsultationActions.prevQuestion
        ),
        tap(() => {
          const contentElement = document.querySelector('.ant-layout-content');
          if (contentElement) {
            contentElement.scrollTop = 0;
          }
        })
      ),
    { dispatch: false }
  );

  prevQuestion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConsultationActions.prevQuestion),
        tap(() => {
          window.scrollTo(0, 0);
        })
      ),
    { dispatch: false }
  );

  completedAllForms$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConsultationActions.completedAllForms),
        withLatestFrom(
          this.store.select(ConsultationSelectors.orderId),
          this.store.select(ConsultationSelectors.getRedirectUrl),
          this.store.select(ConsultationSelectors.getPathwaiHandoffUrl)
        ),
        tap(([action, orderId, redirectUrl, pathwaiHandoffUrl]) => {
          this.gtm.eventTrack('completedAll', {
            category: 'consultation',
            label: 'completedAll',
            gtmCustom: {
              orderId: orderId
            }
          });
          const params = this.activatedRoute.snapshot.queryParams;
          // Legacy questionnaires are done, but migrated ones are still
          // outstanding — Pathwai finishes the chain and honours redirectUrl
          // itself, so it takes priority over every local destination.
          if (pathwaiHandoffUrl) {
            window.location.href = pathwaiHandoffUrl;
          } else if (redirectUrl) {
            window.location.href = redirectUrl;
          } else if (params?.verificationReqs === 'true') {
            this.router.navigate(['verification', 'start'], {
              queryParams: {
                orderId: orderId,
                customerId: params.customerId,
                redirectUri: params.redirectUri
              }
            });
          } else if (orderId) {
            this.router.navigate([
              PATIENT_WEB_PAGES_URLS.account.orders,
              orderId
            ]);
          } else {
            this.router.navigateByUrl('/');
            // this.brandService.goToConsultSuccess(); // redirect to consultation success
          }
        })
      ),
    {
      dispatch: false
    }
  );

  nextForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.nextForm),
      map(() => ConsultationActions.loadRequiredQuestions())
    )
  );

  storeAnswer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.storeAnswer),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getConsultationState),
        this.store.select(ConsultationSelectors.getSelectedQuestionModel)
      ),
      map(([action, state, model]) => {
        const answer = action.selectedAnswer;

        if (model.type === 'file_upload') {
          return ConsultationActions.setFileUploadAnswer();
        } else if (model.type === 'esign') {
          return ConsultationActions.setEsignAnswer();
        } else if (model.type === 'image_list') {
          return ConsultationActions.setImageListAnswer({ answer });
        } else if (
          model.type === 'info' &&
          model.answer['type'] === 'transfer-rx'
        ) {
          return ConsultationActions.setTransferRXAnswer({
            answer
          });
        } else {
          return ConsultationActions.setAnswer({
            answer: {
              field: model.field,
              data: answer
            }
          });
        }
      })
    )
  );

  setFileUploadAnswer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ConsultationActions.setFileUploadAnswer),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getConsultationState),
        this.store.select(ConsultationSelectors.getSelectedQuestionModel)
      ),
      map(([action, state, model]) => {
        const files = [];
        (model.answer as any[]).map(answer => {
          if (state.changedFiles[answer.resourceId]) {
            const fileItem = {
              resourceId: answer.resourceId,
              file: { ...state.files[answer.resourceId] }
            };
            files.push(fileItem);
          }
        });
        return ConsultationActions.saveFiles({ files });
      })
    );
  });

  setEsignAnswer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ConsultationActions.setEsignAnswer),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getConsultationState),
        this.store.select(ConsultationSelectors.getSelectedQuestionModel)
      ),
      switchMap(([action, state, model]) => {
        return this.api.uploadFile(state.files[model.answer.name]);
      }),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getSelectedQuestionModel)
      ),
      map(([res, model]) => {
        return ConsultationActions.setAnswer({
          answer: {
            field: model.field,
            data: { ...model, answer: res }
          }
        });
      })
    );
  });

  setImageListAnswer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ConsultationActions.setImageListAnswer),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getConsultationState),
        this.store.select(ConsultationSelectors.getSelectedQuestionModel)
      ),
      switchMap(([action, state, model]) => {
        const selectedAnswer = action.answer;

        if (selectedAnswer[model.field] === 'yes') {
          const files = [];
          for (let i = 0; i < selectedAnswer.count; i++) {
            const resourceId = model.answer['option'].resourceId + i;
            if (state.changedFiles[resourceId]) {
              files.push({
                resourceId: resourceId,
                file: state.files[resourceId]
              });
            }
          }

          return of(ConsultationActions.saveFiles({ files }));
        } else {
          return of(
            ConsultationActions.setAnswer({
              answer: {
                field: model.field,
                data: { ...selectedAnswer, count: 0 }
              }
            })
          );
        }
      })
    );
  });

  setTransferRXAnswer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ConsultationActions.setTransferRXAnswer),
      withLatestFrom(
        this.store.select(ConsultationSelectors.getConsultationState),
        this.store.select(ConsultationSelectors.getSelectedQuestionModel)
      ),
      mergeMap(([action, state, model]) => {
        const selectedAnswer = action.answer;

        const obs1 = ConsultationActions.setAnswer({
          answer: {
            field: model.field,
            data: selectedAnswer
          }
        });
        let obs2 = null;
        const resourceId = model.answer['option1'].resourceId;
        if (state.changedFiles[resourceId]) {
          const fileItem = {
            field: model.field,
            name: resourceId,
            encoding: 'base64',
            buffer: state.files[resourceId].buffer
          };
          obs2 = ConsultationActions.saveFile({ resourceId, fileItem });
        }

        return [obs1, obs2 ? obs2 : ConsultationActions.nullAction()];
      })
    );
  });

  getFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.getFile),
      switchMap(action =>
        this.api.getFile(action.name).pipe(
          map(file => {
            return ConsultationActions.getFileSuccess({
              file,
              resourceId: action.resourceId
            });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error getting file data.'
            );

            return of(ConsultationActions.getFileFailure({ error }));
          })
        )
      )
    )
  );

  saveFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.saveFiles),
      switchMap(({ files }) => {
        const results = [];
        for (const f of files) {
          const r = this.api
            .uploadFile(f.file)
            .pipe(map(res => ({ resourceId: f.resourceId, file: res })));

          results.push(r);
        }
        return zip(results).pipe(
          map(res => {
            return ConsultationActions.saveFilesSuccess({ files: res });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              notificationMessages.UploadingFilesFailed.title,
              notificationMessages.UploadingFilesFailed.message
            );
            return of(ConsultationActions.saveFilesFailure({ error }));
          })
        );
      })
    )
  );

  saveFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.saveFile),
      switchMap(({ resourceId, fileItem }) =>
        this.api
          .uploadFile(fileItem)
          .pipe(
            map(fileItem =>
              ConsultationActions.saveFileSuccess({ resourceId, fileItem })
            ),
            catchError(error => {
              console.error('Error', error);
              this.notification.error(
                notificationMessages.UploadingFilesFailed.title,
                notificationMessages.UploadingFilesFailed.message
              );
              return of(ConsultationActions.saveFileFailure({ error }));
            })
          )
      )
    )
  );

  deleteDraftConsultation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.deleteDraftConsult),
      switchMap(({ formType }) =>
        this.api.deleteDraftConsultation(formType).pipe(
          map(res => {
            return ConsultationActions.deleteDraftConsultSuccess({
              formType
            });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error delete draft consultation.'
            );
            return of(ConsultationActions.deleteDraftConsultFailure({ error }));
          })
        )
      )
    )
  );
}
